import React from 'react';
import styled from 'styled-components';
import BucketIcon from '@svg/bucket_icon';
import AddBucketIcon from '@svg/add_bucket_icon';
import SyncIcon from '@mui/icons-material/Sync';

import { State, bucketActions, flowActions } from '@store/index';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@common/Button';
import { Alert, Box, Stack } from '@mui/material';
import { LayoutWorkspace, LayoutSidebar } from '@common/layout';
import { ServiceNavigation } from '@components/Workspace/Navigation';
import { Table } from '@common/Table';
import { getBuckets, getDesyncedBuckets, queryBucket, GetBucketsParams, TelnyxBucket } from '@requests/buckets';
import { useMutation, useQuery } from '@tanstack/react-query';
import { GridActionsCellItem, GridDeleteIcon, GridValueFormatterParams } from '@mui/x-data-grid';
import { DocumentLoader } from '@common/hooks/useDocument';
import { useSnackbar } from 'notistack';
import { BucketSearch } from './Search/Search';
import { formatDate } from './Bucket/File';
import { Results } from './Search/Results';
import { ResourceFallback } from '@common/ResourceFallback';
import { useTheme } from '../../../../styles';
import { Resource } from '@store/types';
import { usePermissions } from '@common/hooks/usePermissions';
import { RequestError } from '@requests/common';

const Container = styled.div`
  display: flex;
  height: calc(100vh - 61px);
`;

const LayoutContainer = styled.div`
  display: flex;
  align-items: flex-start;
  height: calc(100vh - 61px);
`;

export const ErrorContainer = styled.div`
  width: calc(100vw - 61px - 240px - 400px);
  height: calc(100vh - 61px);
  padding: 2rem;
`;

interface Props {
  documentLoader: DocumentLoader;
}

export const Buckets: React.FC<Props> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  const { enqueueSnackbar } = useSnackbar();
  const { service_id } = useParams();
  const { admin, permissions, loading: permissionsLoading } = usePermissions(service_id as string, Resource.BUCKET);

  const services = useSelector((state: State) => state.flow.services);
  const service = services.find((service) => service.service_id === service_id);
  const results = useSelector((state: State) => state.buckets.search.results);

  const {
    data: buckets = [],
    error,
    refetch: refetchBuckets,
    isLoading: isBucketLoading,
  } = useQuery<TelnyxBucket[], RequestError, TelnyxBucket[], [string, string, GetBucketsParams]>({
    queryKey: ['globalFiles', 'buckets', { service_id: service_id as string }],
    queryFn: getBuckets,
    staleTime: 15 * 60 * 1000,
  });

  const { data: desynced = [] } = useQuery({
    queryKey: ['globalFiles', 'all_buckets', { service_id: service_id as string }],
    queryFn: getDesyncedBuckets,
    enabled: Boolean(service_id),
  });

  const allowRead = admin || permissions?.read;
  const allowCreate = admin || permissions?.create;
  const allowDelete = admin || permissions?.delete;
  const unauthorized = !allowRead && !permissionsLoading;

  const { mutateAsync: sendQuery, isPending: isQueryLoading } = useMutation({
    mutationFn: queryBucket,
    onMutate: () => {
      dispatch(bucketActions.setSearchLoading(true));
    },
    onSettled: () => {
      dispatch(bucketActions.setSearchLoading(false));
    },
    onSuccess: (data) => {
      if (!data) return;

      if (data?.error) {
        enqueueSnackbar(data?.error?.error || data?.error, {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar('Failed to query vectorstore!', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        return;
      }

      if (data.length === 0) {
        enqueueSnackbar('There was no results found within the expected certainty threshold for your search.', {
          variant: 'warning',
          autoHideDuration: 10000,
        });

        dispatch(bucketActions.storeBucketSearchResults([]));
        return;
      }

      dispatch(bucketActions.storeBucketSearchResults(data));
      enqueueSnackbar('Query sent successfully!', { variant: 'success', autoHideDuration: 3000 });
    },
  });

  const columns = [
    {
      field: 'name',
      headerName: 'Bucket',
      flex: 300,
      sortable: true,
      renderCell: (params: GridValueFormatterParams<string>) => {
        return (
          <Stack direction='row' alignItems='center'>
            <BucketIcon height={14} />
            <Box mr='12px' />

            {params.value}
          </Stack>
        );
      },
    },
    {
      field: 'embedded',
      headerName: 'Embedded',
      flex: 100,
      type: 'boolean',
      sortable: true,
    },
    {
      field: 'region',
      headerName: 'Region',
      flex: 90,
      sortable: true,
    },
    {
      field: 'username',
      headerName: 'Owner',
      flex: 75,
      sortable: true,
    },
    {
      field: 'date_created',
      headerName: 'Created At',
      flex: 130,
      sortable: true,
      valueFormatter: (params: GridValueFormatterParams<string>) => formatDate(params.value),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }: { id: string }) => {
        return [
          <GridActionsCellItem
            key='delete'
            icon={<GridDeleteIcon />}
            label='Delete'
            disabled={!allowDelete}
            onClick={() => {
              dispatch(flowActions.setDeleteBucket(id));
            }}
            color='inherit'
          />,
        ];
      },
    },
  ];

  const bucketEmpty = (
    <ResourceFallback
      resourceName='Bucket'
      resourceIcon={<BucketIcon height={32} color={theme.primary} />}
      resourceButtonIcon={<AddBucketIcon height={14} color={theme.primary} />}
      resourceInfo='A bucket is a collection of files that can be embedded for AI similarity search. You can use buckets to ingest documents into your LLM workflows.'
      onClickCreate={() => dispatch(flowActions.setCreateBucket(true))}
      onClickGuide={() => navigate(`/documentation/buckets`)}
      createDisabled={!allowCreate || permissionsLoading}
      thirdButton={
        <Button
          disabled={!allowCreate || permissionsLoading}
          startIcon={<SyncIcon sx={{ fontSize: '2.2ch !important' }} />}
          sx={{ marginTop: '2rem', svg: { color: theme.text.standard } }}
          onClick={() => dispatch(flowActions.setSyncBuckets(true))}
        >
          Sync Buckets
        </Button>
      }
    />
  );

  const bucketError = (
    <Container>
      <LayoutSidebar
        body={
          <ErrorContainer>
            <Alert
              severity='error'
              action={
                !unauthorized && (
                  <Button onClick={() => refetchBuckets()} color='inherit' size='small'>
                    Retry
                  </Button>
                )
              }
            >
              {unauthorized
                ? 'You do not have permission to view buckets.'
                : error?.response?.data?.error ||
                  error?.message ||
                  'An unexpected error occured whilst trying to load your buckets. Please try again.'}
            </Alert>
          </ErrorContainer>
        }
        rightSidebar={
          <BucketSearch allowRead={Boolean(allowRead)} sendQuery={sendQuery} isQueryLoading={isQueryLoading} />
        }
      />
    </Container>
  );

  const bucketBody = (
    <Container>
      <LayoutSidebar
        body={
          isQueryLoading || results?.length > 0 ? (
            <Results />
          ) : (
            <Table
              // page width - navbar - sidebar - sidebar padding
              width='calc(100vw - 61px - 240px - 400px)'
              height='calc(100vh - 61px)'
              data={buckets}
              columns={columns}
              density={'standard'}
              sortModel='date_created'
              getRowId={(row) => row.name}
              loading={isBucketLoading}
              onRowClick={({ row }) => {
                navigate(`/${service_id}/buckets/${row.name}`);
              }}
            />
          )
        }
        rightSidebar={
          <BucketSearch allowRead={Boolean(allowRead)} sendQuery={sendQuery} isQueryLoading={isQueryLoading} />
        }
      />
    </Container>
  );

  const Body = () => {
    if (error || unauthorized) return bucketError;
    if (buckets.length === 0 && !isBucketLoading) return bucketEmpty;
    return bucketBody;
  };

  const showActions = !(buckets.length === 0 && !isBucketLoading);

  return (
    <LayoutWorkspace
      header={service?.service_name || 'Workspace'}
      body={
        <LayoutContainer>
          <ServiceNavigation />
          <Container>
            <Body />
          </Container>
        </LayoutContainer>
      }
      actions={
        showActions && (
          <Stack direction='row' spacing={1.5}>
            <Button
              startIcon={<SyncIcon sx={{ fontSize: '2.2ch !important' }} />}
              onClick={() => dispatch(flowActions.setSyncBuckets(true))}
              disabled={!desynced?.length || !allowCreate || permissionsLoading}
            >
              Sync Bucket
            </Button>
            <Button
              customColor='black'
              startIcon={<AddBucketIcon height={14} />}
              onClick={() => dispatch(flowActions.setCreateBucket(true))}
              disabled={!allowCreate || permissionsLoading}
            >
              Add Bucket
            </Button>
          </Stack>
        )
      }
    />
  );
};
